// INVARIANT(scriptera): СЦЕНА-ПРИМЕР НАРИСОВАНА В ДВУХ МЕСТАХ — веб (apps/web: components/auth/app-onboarding.tsx,
// components/auth/onboarding.css, components/mock-phone.{tsx,css}, app/[locale]/wizard-scene/[scene]/*,
// lib/scene-script.ts) и iOS (scriptera-app: ios/App/App/Kit/Scenes/*). Правишь вид, шаг, текст-раскладку, тайминг,
// цвет или размер — правь ОБА места тем же днём и сверяй кадры склейкой «веб | iOS» (~/ops/native-scenes). Причина:
// владелец 26.10.2026 — сцены в приложении нативные, «пиксель в пиксель». Канон: ~/projects/NATIVE-SCENES-TZ.md,
// docs/HOW-IT-WORKS.md §«Сцены-примеры».

// КАРТИНКИ ДЕМО-СЦЕНЫ — ОДИН СПИСОК НА ВЕСЬ ПРОДУКТ (владелец 02.09.2026, Н16
// docs/NATIVE-DESIGN-FIXLIST.md: «мокап примера стоит пустым ~0,7 с… грузить заранее,
// чтобы при открытии стоял с первого кадра»).
//
// Сцена «как всё работает» (components/auth/app-onboarding.tsx, Scene) рисует телефон с
// настоящими фото: аватарки людей и картинку публикации. Пока эти файлы не скачаны,
// внутри телефона пусто — это и есть тот «белый экран». Поэтому места, ОТКУДА сцену
// могут открыть (список ботов, окно «+»), греют файлы заранее: к открытию мастера они
// уже лежат в кэше браузера и веб-вью, и сцена встаёт с первого кадра.
//
// ВТОРОГО СПИСКА ПУТЕЙ НЕ ЗАВОДИМ: сцена берёт свои картинки отсюда же, поэтому грев не
// может разойтись с тем, что реально показывается.
//
// МЕТКИ 'use client' ЗДЕСЬ НЕТ НАРОЧНО (О5.9-В): этот же список и этот же хелпер читает
// СЕРВЕР страницы примера `/wizard-scene/<k>` (preload в шапке, встраивание картинок).
// С меткой его экспорты стали бы ссылками на клиент, и сервер не смог бы их позвать.
// Хуков и разметки в файле нет, работа с окном закрыта проверкой `typeof window`.
import { sceneAvatarUrl } from '@/lib/niche-scene';
import { assetUrl } from '@/lib/asset-url';

/** Настоящие фото-аватары ролей сцены (владелец 28.08): берутся с лендинга. */
export const SCENE_AVA = {
  marina: assetUrl('/landing2/ava-daria.png'),
  dasha: assetUrl('/landing2/ava-anastasi.png'),
  maks: assetUrl('/landing2/ava-maksim.jpg'),
} as const;

/** Картинка публикации, когда своей у сцены нет. */
export const SCENE_POST_IMG = assetUrl('/onboarding-post.jpg');

/** ФОТО ПУБЛИКАЦИИ ПО НИШЕ (владелец 23.10.2026: «подобраны для каждой ниши тексты и картинки… качай со
 *  стоков»): слова ниши подставляются в сцену копией дефа, вместе с ними — фото поста той же ниши. Файлы —
 *  `public/scene-post/<ниша>.jpg`, 1080×580 как у `SCENE_POST_IMG`; `sport` — то же фото, что у сцены по
 *  умолчанию. Источник — Pexels (лицензия Pexels: бесплатно, без указания автора), авторы для памяти:
 *  ai — Pexels 8566453, Kindel Media
 *  beauty — Pexels 6635915, cottonbro studio
 *  business — Pexels 5717709, https://kaboompics.com/
 *  creative — Pexels 36697538, Vitaly Gariev
 *  education — Pexels 32410282, 🇻🇳🇻🇳Nguyễn Tiến Thịnh 🇻🇳🇻🇳
 *  fashion — Pexels 36976901, Josue Velasquez
 *  health — Pexels 7579827, cottonbro studio
 *  interior — Pexels 37471990, Yuliya Duzhaya
 *  it — Pexels 34804001, Daniil Komov
 *  kids — Pexels 6274906, https://kaboompics.com/
 *  marketing — Pexels 6593719, Anna Shvets
 *  nutrition — Pexels 5966137, Vanessa Loring
 *  other — Pexels 7657877, Cup of  Couple
 *  psychology — Pexels 6802932, Vlada Karpovich
 *  realty — Pexels 7641900, Alena Darmel
 *  relations — Pexels 4746243, Ketut Subiyanto
 *  travel — Pexels 3885499, Gustavo Fring
 *  sport — фото владельца 28.08 (onboarding-post.jpg). Ниши нет в карте — остаётся `SCENE_POST_IMG`. */
export const SCENE_NICHE_POST: Readonly<Record<string, string>> = {
  ai: '/scene-post/ai.jpg',
  it: '/scene-post/it.jpg',
  marketing: '/scene-post/marketing.jpg',
  business: '/scene-post/business.jpg',
  sport: '/scene-post/sport.jpg',
  nutrition: '/scene-post/nutrition.jpg',
  health: '/scene-post/health.jpg',
  psychology: '/scene-post/psychology.jpg',
  relations: '/scene-post/relations.jpg',
  kids: '/scene-post/kids.jpg',
  education: '/scene-post/education.jpg',
  beauty: '/scene-post/beauty.jpg',
  fashion: '/scene-post/fashion.jpg',
  realty: '/scene-post/realty.jpg',
  interior: '/scene-post/interior.jpg',
  creative: '/scene-post/creative.jpg',
  travel: '/scene-post/travel.jpg',
  other: '/scene-post/other.jpg',
};

/** АВТОР ПУБЛИКАЦИИ ПО НИШЕ (владелец 24.10.2026: «везде постит марина фитнес»): то же лицо (Марина), но ник и
 *  подпись в шапке — по теме ниши; своё имя и ник клиента (profile) главнее. Ник латиницей, один на оба языка. */
export const SCENE_NICHE_PERSON: Readonly<Record<string, { nick: string; ru: string; en: string }>> = {
  ai: { nick: 'daria.ai', ru: 'Дарья Нейросети', en: 'Daria AI' },
  it: { nick: 'daria.dev', ru: 'Дарья Код', en: 'Daria Code' },
  marketing: { nick: 'daria.smm', ru: 'Дарья Маркетинг', en: 'Daria Marketing' },
  business: { nick: 'daria.biz', ru: 'Дарья Бизнес', en: 'Daria Business' },
  sport: { nick: 'daria.fitness', ru: 'Дарья Фитнес', en: 'Daria Fitness' },
  nutrition: { nick: 'daria.food', ru: 'Дарья Питание', en: 'Daria Nutrition' },
  health: { nick: 'daria.health', ru: 'Дарья Здоровье', en: 'Daria Health' },
  psychology: { nick: 'daria.psy', ru: 'Дарья Психолог', en: 'Daria Psychology' },
  relations: { nick: 'daria.family', ru: 'Дарья Отношения', en: 'Daria Family' },
  kids: { nick: 'daria.mama', ru: 'Дарья Мама', en: 'Daria Mom' },
  education: { nick: 'daria.lang', ru: 'Дарья Языки', en: 'Daria Languages' },
  beauty: { nick: 'daria.beauty', ru: 'Дарья Красота', en: 'Daria Beauty' },
  fashion: { nick: 'daria.style', ru: 'Дарья Стиль', en: 'Daria Style' },
  realty: { nick: 'daria.realty', ru: 'Дарья Недвижимость', en: 'Daria Realty' },
  interior: { nick: 'daria.home', ru: 'Дарья Интерьер', en: 'Daria Interior' },
  creative: { nick: 'daria.photo', ru: 'Дарья Фото', en: 'Daria Photo' },
  travel: { nick: 'daria.travel', ru: 'Дарья Путешествия', en: 'Daria Travel' },
  other: { nick: 'daria.blog', ru: 'Дарья Блог', en: 'Daria Blog' },
};

/** Ник и подпись автора для ниши; неизвестная ниша — пусто (остаются свои у сцены). */
export function nichePerson(nicheKey: string | null | undefined, locale: string): { nick: string; name: string } | null {
  const p = nicheKey ? SCENE_NICHE_PERSON[nicheKey] : undefined;
  return p ? { nick: p.nick, name: locale === 'en' ? p.en : p.ru } : null;
}

/** Фото поста для ниши; неизвестная ниша — общее фото сцены. */
/**
 * ВТОРАЯ КАРТИНКА ПОСТА — У «ПРОДАЖИ» (QUICK-START §11.34 п.4, владелец 30.10: «хотя бы картинки у постов пусть будут
 * разные, чтобы человек понимал, что там его что-то другое ждёт»). Новых фото не заводим: у сцены g2 — фото соседней по
 * смыслу ниши из того же набора (`SCENE_NICHE_POST`), всегда не то, что у g1 этой ниши. Замок — тест
 * `scene-assets.test.ts` (для каждой ниши g1 ≠ g2).
 */
export const SCENE_NICHE_POST_SALE: Readonly<Record<string, string>> = {
  ai: SCENE_NICHE_POST.it!,
  it: SCENE_NICHE_POST.ai!,
  marketing: SCENE_NICHE_POST.business!,
  business: SCENE_NICHE_POST.marketing!,
  sport: SCENE_NICHE_POST.health!,
  nutrition: SCENE_NICHE_POST.health!,
  health: SCENE_NICHE_POST.nutrition!,
  psychology: SCENE_NICHE_POST.relations!,
  relations: SCENE_NICHE_POST.psychology!,
  kids: SCENE_NICHE_POST.education!,
  education: SCENE_NICHE_POST.kids!,
  beauty: SCENE_NICHE_POST.fashion!,
  fashion: SCENE_NICHE_POST.beauty!,
  realty: SCENE_NICHE_POST.interior!,
  interior: SCENE_NICHE_POST.realty!,
  creative: SCENE_NICHE_POST.fashion!,
  travel: SCENE_NICHE_POST.other!,
  other: SCENE_NICHE_POST.travel!,
};

/** Картинка поста сцены «Продажи» (g2) для ниши; ниши нет в карте — фото, отличное от `SCENE_POST_IMG`. */
export function nicheSalePostImg(nicheKey: string | null | undefined): string {
  const own = nicheKey ? SCENE_NICHE_POST_SALE[nicheKey] : undefined;
  return assetUrl(own ?? SCENE_NICHE_POST.business!);
}

export function nichePostImg(nicheKey: string | null | undefined): string {
  const own = nicheKey ? SCENE_NICHE_POST[nicheKey] : undefined;
  // Версия в адресе (asset-url.ts): файл держится в браузере без сверки до следующей выкатки.
  return own ? assetUrl(own) : SCENE_POST_IMG;
}

/** Аватарка бота в телеграм-сцене, когда своей нет. */
export const SCENE_BOT_AVA = assetUrl('/onboarding-bot.png');

/** Всё, что сцена может показать без данных человека. Список открыт наружу: страница
 *  примера для приложения (`/wizard-scene/<k>`) кладёт эти же файлы в `<link rel=preload>`
 *  своей шапки — там грева из кабинета нет, веб-вью открывается вхолодную. */
/* Обоев-картинки у Телеграм-сцен больше нет (владелец 22.09.2026): фон корпуса —
 * CSS-градиент из кадров App Store (onboarding.css `.onb-phone.tg`), качать нечего. */

/** Всё, что сцена может показать без данных человека. */
export const SCENE_STATIC: readonly string[] = [
  ...Object.values(SCENE_AVA),
  SCENE_POST_IMG,
  SCENE_BOT_AVA,
];

// ── ЛИЧНЫЕ КАРТИНКИ ЧЕЛОВЕКА (О5.9-В, владелец 03.09.2026: «всё, что телефон
// показывает, загружено заранее») ──
// Пример в мастере показывает НЕ только главный портрет: аккаунт берётся под тип сети
// шаблона и меняется при смене аккаунта в анкете, а в комментариях стоят лица его
// аудитории. Поэтому греть надо картинки ВСЕХ аккаунтов из ответа `GET /api/user/niche`,
// а не одного — иначе при открытии другого шаблона аватарка и публикация приезжают уже
// на глазах у человека (видео владельца 03.09: догрузка через ~0,5 с).

/** Часть ответа `GET /api/user/niche`, в которой лежат картинки (структурно — `NicheApi`;
 *  описано полями, чтобы хелпер оставался чистым и проверялся юнитами). */
export interface PersonalAssetsSource {
  /** Ниша человека: фото публикации ниши (`nichePostImg`) сцена ставит, когда своей публикации нет. */
  nicheKey?: string | null;
  suggest?: { nicheKey?: string | null } | null;
  profile?: {
    avatar?: string | null;
    postImg?: string | null;
    faces?: readonly { avatar?: string | null }[] | null;
  } | null;
  accounts?:
    | readonly {
        avatarUrl?: string | null;
        postImg?: string | null;
        faces?: readonly { avatar?: string | null }[] | null;
      }[]
    | null;
}

/**
 * Адреса ВСЕХ личных картинок примера: аватарки аккаунтов, картинки их публикаций и лица
 * аудитории, плюс главный портрет. Аватарка аккаунта переводится в публичную ручку
 * (`sceneAvatarUrl`) — тем же адресом её просит и сама сцена, поэтому грев попадает в тот
 * же кэш. Порядок сохраняется, повторы убираются. Чистая — юниты.
 */
export function personalSceneAssets(data: PersonalAssetsSource | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (url: string | null | undefined): void => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(url);
  };
  const faces = (list: readonly { avatar?: string | null }[] | null | undefined): void => {
    for (const f of list ?? []) add(f?.avatar);
  };
  // Фото поста ниши (справка, мастер): без него телефон-сцена стояла с пустой публикацией ~1 с (обход 25.10.2026).
  const niche = data?.nicheKey ?? data?.suggest?.nicheKey ?? null;
  if (niche) add(nichePostImg(niche));
  if (data?.profile) {
    add(sceneAvatarUrl(data.profile.avatar));
    add(data.profile.postImg);
    faces(data.profile.faces);
  }
  for (const acc of data?.accounts ?? []) {
    add(sceneAvatarUrl(acc.avatarUrl));
    add(acc.postImg);
    faces(acc.faces);
  }
  return out;
}

/** Уже гретые адреса: грев идёт один раз на загрузку страницы. */
const warmed = new Set<string>();
/**
 * ГРЕТЫЕ КАРТИНКИ ДЕРЖИМ ЖИВЫМИ (ТЗ HELP-WARM-FIX §5, замер 26.10.2026: фото сцены при открытии статьи шло в сеть, хотя
 * было скачано заранее). Браузер отдаёт <img> из памяти страницы сразу, пока жив объект, который её загрузил; собрал
 * его сборщик мусора — картинка снова идёт по HTTP-кэшу (а у файлов `public/` срок `max-age=0` — сверка с сервером).
 * Держим ссылки до перезагрузки страницы: файлов сцены — десяток, меньше мегабайта.
 */
export const heldImages: HTMLImageElement[] = [];

/**
 * Скачать картинки сцены заранее (браузер положит их в свой кэш). Зовёт ТОЛЬКО исполнитель плана прогрева
 * (components/cabinet/warm-cache.tsx, цель `scene.photos` — У1 на входе в кабинет, ТЗ HELP-WARM-FIX §5).
 * `extra` — картинки ЭТОГО человека (его аватарка, публикация по нише): адреса известны только после ответа ручки ниши.
 * Промис — когда все встали или упали (очередь прогрева ждёт его, как ответ ручки). Не скачалось — сцена покажет то же.
 */
export function warmSceneAssets(extra: readonly (string | null | undefined)[] = []): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const loads: Promise<void>[] = [];
  for (const url of [...SCENE_STATIC, ...extra]) {
    if (!url || warmed.has(url)) continue;
    warmed.add(url);
    const img = new Image();
    heldImages.push(img);
    img.decoding = 'async';
    // ВАЖНОСТЬ — ВЫСОКАЯ (замер 02.09.2026): по умолчанию браузер даёт картинке низкую важность, и грев вставал в
    // самый конец очереди запросов страницы. Файлы сцены все вместе — меньше мегабайта.
    img.fetchPriority = 'high';
    loads.push(
      new Promise<void>((done) => {
        img.onload = () => done();
        img.onerror = () => done();
      }),
    );
    img.src = url;
  }
  return Promise.all(loads).then(() => undefined);
}
